import { useSelector, useDispatch } from "react-redux";
import { get as _get } from "lodash";
import { BigNumber } from "bignumber.js";
// components mui
import { Alert, Box, Button, Modal, Typography } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
// Actions
import { setModalData, setModal } from "../../../redux/actions/modals";

// components
import ModalHeader from "../ModalHeader";
import TokenLogo from "../../TokenLogo";
import DetailRow from "../../DetailRow";
import SwapRoute from "../../SwapRoute";
import { amountType } from "../../../theme/main";

// price impact levels, same as the warning on the swap page
const IMPACT_WARNING = 15;
const IMPACT_DANGER = 30;

const AmountRow = ({ token, amount, label }) => (
  <Box sx={{ p: 2, borderRadius: "14px", bgcolor: "background.light" }}>
    <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
      <Typography component="p" sx={{ ...amountType, fontSize: "1.75rem", minWidth: 0 }} noWrap>
        {amount}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        <TokenLogo token={token} size={28} />
        <Typography sx={{ fontWeight: 600 }} component="span">
          {_get(token, "symbol", "")}
        </Typography>
      </Box>
    </Box>
  </Box>
);

/**
 * Summary shown before signing a swap
 * modal data: { onConfirm, tokenSend, tokenReceived, amountSend, amountReceived, exactIn, limit, route, priceImpact, slippage, lpFee }
 */
const ModalConfirmSwap = () => {
  const dispatch = useDispatch();
  const currentModal = useSelector((state) => state.modals.modal);
  const data = useSelector((state) => state.modals.data) || {};

  const impact = new BigNumber(_get(data, "priceImpact", "0"));
  const danger = impact.isGreaterThanOrEqualTo(IMPACT_DANGER);
  const warning = impact.isGreaterThanOrEqualTo(IMPACT_WARNING);

  const closeModal = () => {
    dispatch(setModal(null));
    dispatch(setModalData(null));
  };

  const confirm = () => {
    const onConfirm = _get(data, "onConfirm", null);
    closeModal();
    if (onConfirm) onConfirm();
  };

  return (
    <Modal open={currentModal === "ConfirmSwap"} onClose={closeModal} sx={{ display: "flex", p: 2 }}>
      <Box sx={{ width: "100%", maxWidth: 460, m: "auto", maxHeight: "calc(100vh - 32px)", overflowY: "auto" }}>
        <ModalHeader title={"Confirm swap"} closeFunction={closeModal} />
        {data.onConfirm ? (
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <AmountRow
              label={data.exactIn ? "You pay" : "You pay (estimated)"}
              token={data.tokenSend}
              amount={data.amountSend}
            />
            <Box sx={{ display: "flex", justifyContent: "center", my: "-14px", position: "relative", zIndex: 1 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "12px",
                  bgcolor: "background.light",
                  border: "4px solid",
                  borderColor: "background.paper",
                  color: "text.secondary",
                }}
              >
                <ArrowDownwardIcon sx={{ fontSize: 18 }} />
              </Box>
            </Box>
            <AmountRow
              label={data.exactIn ? "You receive (estimated)" : "You receive"}
              token={data.tokenReceived}
              amount={data.amountReceived}
            />

            <Typography variant="body2" component="p" sx={{ color: "text.secondary", my: 2 }}>
              {data.exactIn
                ? `The output is estimated. You will receive at least ${data.limit} ${_get(data, "tokenReceived.symbol", "")} or the transaction reverts.`
                : `The input is estimated. You will pay at most ${data.limit} ${_get(data, "tokenSend.symbol", "")} or the transaction reverts.`}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 2, borderTop: 1, borderColor: "divider" }}>
              <DetailRow
                label={data.exactIn ? "Minimum received" : "Maximum sold"}
                value={`${data.limit} ${_get(data, data.exactIn ? "tokenReceived.symbol" : "tokenSend.symbol", "")}`}
              />
              <DetailRow label="Price impact" value={`~${impact.toFixed(2)}%`} />
              <DetailRow label="Slippage tolerance" value={`${data.slippage}%`} />
              <DetailRow label="Liquidity provider fee" value={`${data.lpFee} ${_get(data, "tokenSend.symbol", "")}`} />
              {data.path ? (
                <SwapRoute path={data.path} tokenSend={data.tokenSend} tokenReceived={data.tokenReceived} />
              ) : (
                <DetailRow label="Route" value={_get(data, "route", []).join(" > ")} />
              )}
            </Box>

            {warning ? (
              <Alert severity={danger ? "error" : "warning"} sx={{ mt: 2 }}>
                {danger
                  ? `The price impact is very high (~${impact.toFixed(2)}%). You may lose a large part of this trade.`
                  : `The price impact is high (~${impact.toFixed(2)}%).`}
              </Alert>
            ) : null}

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 2.5 }}>
              <Button size="large" onClick={closeModal} variant="outlined">
                Cancel
              </Button>
              <Button size="large" onClick={confirm} variant="contained" color={danger ? "error" : "primary"}>
                {danger ? "Swap anyway" : "Confirm swap"}
              </Button>
            </Box>
          </Box>
        ) : null}
      </Box>
    </Modal>
  );
};

export default ModalConfirmSwap;
